import React, { useState, useEffect } from 'react';
import { useFoodSafetyContext, FS_SCREENS } from '../context/FoodSafetyContext';
import { foodSafetyService } from '../services/foodSafetyService';
import moment from 'moment';

const PPEChecklist: React.FC = () => {
    const { state, dispatch } = useFoodSafetyContext();
    const [area, setArea] = useState('Mixing + Oven');
    const [sampleSize, setSampleSize] = useState<number>(50);
    const [totalPossibleDefects, setTotalPossibleDefects] = useState<number>(2);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const checklistCriteria = [
        "Hair Net Wearing Issue / Beard Net",
        "Apron (Torn, Dirty)",
        "Personal Hygiene (Nail without trimming/paint, Heena application)",
        "Jewellery Policy (Bracelet, Thread, Ring, Bindi)"
    ];

    const [defectCounts, setDefectCounts] = useState<number[]>(new Array(checklistCriteria.length).fill(0));

    useEffect(() => {
        if (area === 'Mixing + Oven') setSampleSize(50);
        else if (area === 'Packing') setSampleSize(100);
    }, [area]);

    const hasLoadedRef = React.useRef<string | null>(null);

    // Resume logic
    useEffect(() => {
        const loadResumedData = async () => {
            if (state.isPaused && state.currentTourId && hasLoadedRef.current !== state.currentTourId) {
                hasLoadedRef.current = state.currentTourId;
                try {
                    const items = await foodSafetyService.getChecklistItems(state.currentTourId);
                    if (items && items.length > 0) {
                        const newCounts = new Array(checklistCriteria.length).fill(0);
                        items.forEach((item: any) => {
                            const idx = checklistCriteria.indexOf(item.cr953_food_safety_criteria);
                            if (idx !== -1 && item.cr953_food_safety_defectcount) {
                                newCounts[idx] = parseInt(item.cr953_food_safety_defectcount, 10) || 0;
                            }
                        });
                        setDefectCounts(newCounts);
                        
                        if (items[0].cr953_food_safety_area) {
                            setArea(items[0].cr953_food_safety_area);
                        }
                    }
                } catch (error) {
                    console.error("Failed to load paused checklist items", error);
                }
            }
        };
        loadResumedData();
    }, [state.isPaused, state.currentTourId]);

    const handleCountChange = (index: number, val: string) => {
        let count = parseInt(val, 10);
        if (isNaN(count)) count = 0;
        if (count < 0) count = 0;
        if (count > sampleSize) count = sampleSize;

        const newCounts = [...defectCounts];
        newCounts[index] = count;
        setDefectCounts(newCounts);
    };

    const totalDefects = defectCounts.reduce((sum, count) => sum + count, 0);
    const compliancePercent = sampleSize > 0 ? Math.max(0, ((sampleSize - totalDefects) / sampleSize) * 100) : 100;
    const finalScore = compliancePercent.toFixed(2) + "%";
    const isPass = compliancePercent >= 80;

    const filledFields = checklistCriteria.length;
    const progressPercent = 100;

    const saveRows = async () => {
        if (!state.currentTourId || !state.sessionData) return;
        
        await foodSafetyService.cleanChecklistItems(state.currentTourId);

        const tourDate = state.sessionData.tourStartDate || new Date();
        const dateStr = moment(tourDate).format("MM-DD-YYYY");
        const timeStr = moment(tourDate).format("hh:mm A");

        for (let i = 0; i < checklistCriteria.length; i++) {
            const count = defectCounts[i];
            const category = count === 0 ? "Compliant" : "Non-Compliant";

            const rowPayload = {
                cr953_food_safety_title: `PPE_${state.sessionData.site}_${state.sessionData.line}_${dateStr}`,
                "cr953_food_safety_tourid@odata.bind": `/cr3ea_prod_rajpura_quality_tours(${state.currentTourId})`,
                cr953_food_safety_checklisttype: "PPE Checklist",
                cr953_food_safety_manufacturingsite: state.sessionData.site,
                cr953_food_safety_line: state.sessionData.line,
                cr953_food_safety_qaexecutive: state.sessionData.qaExecutive,
                cr953_food_safety_productionincharge: state.sessionData.productionIncharge,
                cr953_food_safety_area: area,
                cr953_food_safety_criteria: checklistCriteria[i],
                cr953_food_safety_cycle: state.sessionData.cycle,
                cr953_food_safety_defectcategory: category,
                cr953_food_safety_defectcount: count.toString(),
                cr953_food_safety_samplesize: sampleSize.toString(),
                cr953_food_safety_totalpossibledefects: totalPossibleDefects.toString(),
                cr953_food_safety_date: dateStr,
                cr953_food_safety_time: timeStr
            };

            await foodSafetyService.saveChecklistRow(rowPayload);
        }
    };

    const handlePause = async () => {
        if (!state.currentTourId) return;
        const confirmPause = window.confirm("Are you sure you want to pause this audit session? Your entered details will be saved, and you can resume later.");
        if (!confirmPause) return;

        setIsSubmitting(true);
        dispatch({ type: 'SET_LOADING', payload: true });

        try {
            await saveRows();
            
            const updatePayload = {
                cr3ea_prod_rajpura_quality_tourid: state.currentTourId,
                cr3ea_status: "InProgress-paused",
                cr3ea_food_safety_area: area,
                cr3ea_food_safety_samplesize: sampleSize.toString(),
                cr3ea_food_safety_totalpossibledefects: totalPossibleDefects.toString(),
                cr3ea_food_safety_totaldefects: totalDefects.toString()
            };
            await foodSafetyService.saveSession(updatePayload);
            
            dispatch({ type: 'SET_SCREEN', payload: FS_SCREENS.CHECKLIST_INFO });
        } catch (error) {
            console.error("Error pausing PPE Checklist:", error);
            alert("Failed to pause checklist.");
        } finally {
            setIsSubmitting(false);
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };

    const handleSubmit = async () => {
        if (!state.currentTourId || !state.sessionData) {
            alert("Session missing. Please start again.");
            return;
        }

        setIsSubmitting(true);
        dispatch({ type: 'SET_LOADING', payload: true });

        try {
            await saveRows();

            const passFail = isPass ? "Pass" : "Fail";
            const updatePayload = {
                cr3ea_prod_rajpura_quality_tourid: state.currentTourId,
                cr3ea_overall_score: finalScore,
                cr3ea_checklist_result: passFail,
                cr3ea_status: "Submitted",
                cr3ea_tourcompletiondate: new Date().toISOString(),
                cr3ea_food_safety_area: area,
                cr3ea_food_safety_samplesize: sampleSize.toString(),
                cr3ea_food_safety_totalpossibledefects: totalPossibleDefects.toString(),
                cr3ea_food_safety_totaldefects: totalDefects.toString()
            };

            await foodSafetyService.saveSession(updatePayload);

            dispatch({ type: 'SET_SCREEN', payload: FS_SCREENS.SUMMARY });

        } catch (error: any) {
            console.error("Error submitting PPE Checklist:", error);
            alert("Failed to submit checklist. Please check your connection.");
        } finally {
            setIsSubmitting(false);
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };

    return (
        <div>
            {/* Stepper Workflow Bar */}
            <div style={{ marginBottom: '20px', background: '#f8fafc', border: '1px solid #e2e8f0', padding: '10px 16px', borderRadius: '6px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', backgroundColor: '#e2e8f0', color: '#475569', padding: '4px 10px', borderRadius: '4px', fontWeight: 600, fontSize: '13px' }}>
                        1. Request Info
                    </span>
                    <span style={{ color: '#94a3b8' }}>➔</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', backgroundColor: '#80cbcf', color: '#ffffff', padding: '4px 12px', borderRadius: '4px', fontWeight: 600, fontSize: '13px' }}>
                        2. PPE Checklist
                    </span>
                </div>
            </div>

            {/* PPE Progress Tracker Banner */}
            <div style={{ padding: '20px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#f8fafc', marginBottom: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px', marginBottom: '15px' }}>
                    <div>
                        <h5 style={{ margin: 0, fontWeight: 700, color: '#1e293b', fontSize: '15px' }}>
                            PPE Progress Tracker
                        </h5>
                        <p style={{ margin: '3px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                            {filledFields} / {checklistCriteria.length} checkpoints filled ({progressPercent}%)
                        </p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <div style={{ textAlign: 'right' }}>
                            <span style={{ fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 600, letterSpacing: '0.5px' }}>Current Score</span>
                            <div style={{ fontSize: '20px', fontWeight: 800, color: isPass ? '#16a34a' : '#dc2626', marginTop: '2px' }}>{finalScore}</div>
                        </div>
                        <div style={{ padding: '4px 12px', borderRadius: '9999px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', backgroundColor: isPass ? '#dcfce7' : '#fee2e2', color: isPass ? '#15803d' : '#b91c1c', border: `1px solid ${isPass ? '#bbf7d0' : '#fecaca'}` }}>
                            {isPass ? 'Pass' : 'Fail'}
                        </div>
                    </div>
                </div>

                <div style={{ height: '8px', backgroundColor: '#e2e8f0', borderRadius: '9999px', overflow: 'hidden', marginBottom: '15px' }}>
                    <div style={{ width: `${progressPercent}%`, height: '100%', backgroundColor: isPass ? '#16a34a' : '#dc2626', transition: 'width 0.3s ease' }}></div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px' }}>
                    <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', padding: '10px', borderRadius: '6px', textAlign: 'center' }}>
                        <span style={{ fontSize: '11px', textTransform: 'uppercase', color: '#16a34a', fontWeight: 700 }}>Sample Size</span>
                        <div style={{ fontSize: '18px', fontWeight: 700, color: '#16a34a', marginTop: '2px' }}>{sampleSize}</div>
                    </div>
                    <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', padding: '10px', borderRadius: '6px', textAlign: 'center' }}>
                        <span style={{ fontSize: '11px', textTransform: 'uppercase', color: '#dc2626', fontWeight: 700 }}>Total Defects</span>
                        <div style={{ fontSize: '18px', fontWeight: 700, color: '#dc2626', marginTop: '2px' }}>{totalDefects}</div>
                    </div>
                    <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', padding: '10px', borderRadius: '6px', textAlign: 'center' }}>
                        <span style={{ fontSize: '11px', textTransform: 'uppercase', color: '#0284c7', fontWeight: 700 }}>Possible Defects</span>
                        <div style={{ fontSize: '18px', fontWeight: 700, color: '#0284c7', marginTop: '2px' }}>{totalPossibleDefects}</div>
                    </div>
                </div>
            </div>

            {/* PPE Audit Details */}
            <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '20px', marginBottom: '24px' }}>
                <h5 style={{ fontWeight: 700, color: '#0f172a', fontSize: '15px', marginBottom: '16px' }}>
                    PPE Audit Details
                </h5>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '18px' }}>
                    <div>
                        <label style={{ display: 'block', fontSize: '13px', color: '#475569', fontWeight: 600, marginBottom: '6px' }}>Area</label>
                        <select className="form-select" value={area} onChange={e => setArea(e.target.value)} style={{ height: '42px', fontSize: '14px', border: '1px solid #cbd5e1', borderRadius: '6px' }}>
                            <option value="Mixing + Oven">Mixing + Oven</option>
                            <option value="Packing">Packing</option>
                        </select>
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: '13px', color: '#475569', fontWeight: 600, marginBottom: '6px' }}>Sample Size</label>
                        <input type="number" className="form-control" value={sampleSize} onChange={e => setSampleSize(parseInt(e.target.value) || 0)} style={{ height: '42px', fontSize: '14px', border: '1px solid #cbd5e1', borderRadius: '6px' }} />
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: '13px', color: '#475569', fontWeight: 600, marginBottom: '6px' }}>Total Possible Defects</label>
                        <input type="number" className="form-control" value={totalPossibleDefects} onChange={e => setTotalPossibleDefects(parseInt(e.target.value) || 0)} style={{ height: '42px', fontSize: '14px', border: '1px solid #cbd5e1', borderRadius: '6px' }} />
                    </div>
                </div>
            </div>

            {/* PPE Checkpoints Table */}
            <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '20px', marginBottom: '24px' }}>
                <h5 style={{ fontWeight: 700, color: '#0f172a', fontSize: '15px', marginBottom: '16px' }}>
                    PPE Checkpoints Table
                </h5>
                <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'center', fontSize: '13px' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                <th style={{ padding: '12px 14px', width: '5%', color: '#475569', fontWeight: 700 }}>No.</th>
                                <th style={{ padding: '12px 14px', width: '70%', textAlign: 'left', color: '#475569', fontWeight: 700 }}>Checkpoint Criteria</th>
                                <th style={{ padding: '12px 14px', width: '25%', color: '#475569', fontWeight: 700 }}>Defect Count (Max {sampleSize})</th>
                            </tr>
                        </thead>
                        <tbody>
                            {checklistCriteria.map((criteria, index) => (
                                <tr key={index} style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: index % 2 === 0 ? '#ffffff' : '#fcfdfe' }}>
                                    <td style={{ padding: '12px 14px', fontWeight: 600, color: '#475569' }}>{index + 1}</td>
                                    <td style={{ padding: '12px 14px', textAlign: 'left', color: '#1e293b', fontWeight: 500 }}>{criteria}</td>
                                    <td style={{ padding: '12px 14px' }}>
                                        <input 
                                            type="number" 
                                            className="form-control" 
                                            value={defectCounts[index] || 0} 
                                            min="0" 
                                            max={sampleSize}
                                            onChange={e => handleCountChange(index, e.target.value)}
                                            style={{ width: '100px', margin: '0 auto', textAlign: 'center', height: '38px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr style={{ backgroundColor: '#f8fafc', borderTop: '2px solid #e2e8f0', fontWeight: 700 }}>
                                <td colSpan={2} style={{ padding: '12px 14px', textAlign: 'right', color: '#475569' }}>Total Defects:</td>
                                <td style={{ padding: '12px 14px', color: '#dc2626', fontSize: '15px' }}>{totalDefects}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>

            {/* Bottom Actions */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
                <button
                    type="button"
                    onClick={handlePause}
                    disabled={isSubmitting}
                    style={{
                        backgroundColor: '#f8fafc',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        color: '#475569',
                        padding: '8px 20px',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer'
                    }}
                >
                    <i className="bi bi-pause-circle me-2 text-warning"></i>
                    Pause Audit
                </button>

                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    style={{
                        backgroundColor: '#80cbcf',
                        border: 'none',
                        borderRadius: '6px',
                        color: '#ffffff',
                        padding: '8px 24px',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer'
                    }}
                >
                    {isSubmitting ? 'Submitting...' : 'Submit Checklist'}
                </button>
            </div>
        </div>
    );
};

export default PPEChecklist;
